// src/pages/CtcScreeningFormPage.js
// Routes: /ctc-screenings/new (create) and /ctc-screenings/:id/edit (edit).
//
// Same EMPTY_FORM + single set(k) closure pattern as PatientFormPage.js.
// Validation is deliberately minimal — first name only — because camp intake
// is often incomplete; the backend accepts everything else as optional. Last
// name is optional too, but is needed before a record can be converted to a
// CTC patient.

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppShell } from '../components/Layout';
import { ctcScreeningAPI } from '../services/api';
import { EDITABLE_STATUSES, CASE_FLAGS, statusMeta } from '../utils/ctcScreening';

import Icon from '../components/Icon';
const TEXT_FIELDS = [
  'firstName', 'lastName', 'phone', 'email', 'citizenNumber',
  'district', 'address', 'maritalStatus', 'emergencyContact', 'guardianName',
  'referralSource', 'campaignCoordinator', 'organizerName', 'organizerRelationship',
  'organizerEmail', 'organizerPhone', 'campName', 'campLocation', 'markers',
  'oralExaminationResult', 'breastExaminationResult', 'papSmearResult',
  'cervicalInfections', 'overallResult', 'campRemarks', 'notes',
];

// Blank must go to the backend as null, never '': gender is parsed with
// Gender.valueOf (so '' is a 400), and '' is not a valid LocalDate / Integer.
const NULL_WHEN_BLANK = ['dateOfBirth', 'ageAtScreening', 'gender', 'registeredDate'];

// Text fields that go as null when blank on edit too, not just on create.
// lastName is optional, and a blank one must not be stored as ''.
const NULL_ON_EDIT_WHEN_BLANK = ['lastName'];

// aadharNumber is handled like a password-change field, not a TEXT_FIELD:
// responses carry only aadharNumberMasked ("•••• •••• 4821"), so the input is
// never pre-filled on edit, and it is sent only when the navigator types a new
// number. Pre-filling it would submit the masked string over the real number
// the first time anyone saved without touching the field.

const BOOLEAN_FIELDS = [
  'oralExaminationDone', 'breastExaminationDone', 'papSmearDone',
  ...CASE_FLAGS.map(f => f.key),
];

const EMPTY_FORM = {
  ...Object.fromEntries(TEXT_FIELDS.map(k => [k, ''])),
  ...Object.fromEntries(NULL_WHEN_BLANK.map(k => [k, ''])),
  ...Object.fromEntries(BOOLEAN_FIELDS.map(k => [k, false])),
  aadharNumber: '',
  status: 'SCREENED',
};

const EXAMS = [
  { done: 'oralExaminationDone',   result: 'oralExaminationResult',   label: 'Oral examination' },
  { done: 'breastExaminationDone', result: 'breastExaminationResult', label: 'Breast examination (CBE)' },
  { done: 'papSmearDone',          result: 'papSmearResult',          label: 'PAP smear' },
];

const MODALITIES = [...new Set(CASE_FLAGS.map(f => f.modality))];

function toForm(r) {
  return {
    ...Object.fromEntries(TEXT_FIELDS.map(k => [k, r[k] || ''])),
    dateOfBirth:    r.dateOfBirth?.slice(0, 10) || '',
    registeredDate: r.registeredDate?.slice(0, 10) || '',
    ageAtScreening: r.ageAtScreening ?? '',
    gender:         r.gender || '',
    ...Object.fromEntries(BOOLEAN_FIELDS.map(k => [k, Boolean(r[k])])),
    aadharNumber:   '',
    status:         r.status || 'SCREENED',
  };
}

function toPayload(form, { isEdit, isConverted }) {
  const payload = {};
  // Create: blank text is simply absent (null), so no empty strings get stored.
  // Edit: blank text is sent as '' — the backend's partial update skips nulls,
  // so '' is the only way a navigator can clear a field they filled in wrongly.
  //
  // KNOWN LIMITATION (backend, not fixed here): the NULL_WHEN_BLANK fields —
  // dateOfBirth, ageAtScreening, gender, registeredDate — have no '' escape
  // hatch, so once set they cannot be cleared from this form. Blanking one
  // sends null, which CtcScreeningService.updateScreeningRecord reads as
  // "unchanged". Fixing it needs an explicit clear signal on the backend.
  //
  // lastName (NULL_ON_EDIT_WHEN_BLANK) is the exception among text fields:
  // blank is null on edit too, so a stored last name can't be cleared here.
  //
  // aadharNumber follows the same blank-means-unchanged rule, on both create
  // and edit: blank is left out of the payload entirely. So an Aadhar number,
  // once stored, can be replaced from this form but not cleared.
  TEXT_FIELDS.forEach(k => {
    const nullWhenBlank = !isEdit || NULL_ON_EDIT_WHEN_BLANK.includes(k);
    payload[k] = form[k] === '' && nullWhenBlank ? null : form[k];
  });
  if (form.aadharNumber.trim() !== '') payload.aadharNumber = form.aadharNumber.trim();
  NULL_WHEN_BLANK.forEach(k => { payload[k] = form[k] === '' ? null : form[k]; });
  if (payload.ageAtScreening != null) payload.ageAtScreening = Number(payload.ageAtScreening);
  BOOLEAN_FIELDS.forEach(k => { payload[k] = form[k]; });
  // CreateRequest has no status (new records start SCREENED), and a converted
  // record's status is owned by the convert action — the backend rejects it here.
  if (isEdit && !isConverted) payload.status = form.status;
  return payload;
}

export default function CtcScreeningFormPage() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const isEdit   = Boolean(id);

  const [form,        setForm]        = useState(EMPTY_FORM);
  const [isConverted, setIsConverted] = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState('');

  // What's on file, shown beside the (always blank) Aadhar input. A revealed
  // value is display-only — it must never be copied into form.aadharNumber.
  const [aadharMasked,    setAadharMasked]    = useState(null);
  const [aadharRevealed,  setAadharRevealed]  = useState(null);
  const [aadharRevealing, setAadharRevealing] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    ctcScreeningAPI.get(id).then(r => {
      setForm(toForm(r.data));
      setAadharMasked(r.data.aadharNumberMasked || null);
      setIsConverted(r.data.status === 'CONVERTED_TO_PATIENT');
    }).catch(() => navigate('/ctc-screenings'));
  }, [id, isEdit, navigate]);

  const set = (k) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(f => ({ ...f, [k]: val }));
  };

  // Same audited endpoint as the detail page's Show — only on an explicit click.
  const toggleAadhar = async () => {
    if (aadharRevealed !== null) { setAadharRevealed(null); return; }
    setAadharRevealing(true);
    try {
      const { data } = await ctcScreeningAPI.getAadharNumber(id);
      setAadharRevealed(data.aadharNumber || '');
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load the current Aadhar number.');
    } finally {
      setAadharRevealing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = toPayload(form, { isEdit, isConverted });
      if (isEdit) {
        await ctcScreeningAPI.update(id, payload);
        navigate(`/ctc-screenings/${id}`);
      } else {
        const res = await ctcScreeningAPI.create(payload);
        navigate(`/ctc-screenings/${res.data.id}`);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save screening. Please check the form.');
      setSaving(false);
    }
  };

  const fullName = `${form.firstName} ${form.lastName}`.trim();
  const breadcrumbs = isEdit
    ? [{ label: 'CTC Screenings', to: '/ctc-screenings' }, { label: fullName || 'Screening', to: `/ctc-screenings/${id}` }, { label: 'Edit' }]
    : [{ label: 'CTC Screenings', to: '/ctc-screenings' }, { label: 'Add Screening' }];

  const input = (k, label, props = {}) => (
    <div className={`form-group${props.full ? ' form-full' : ''}`}>
      <label className="form-label">{label}{props.required && <span className="required">*</span>}</label>
      <input className="form-input" value={form[k]} onChange={set(k)}
        type={props.type || 'text'} placeholder={props.placeholder} required={props.required}
        min={props.min} max={props.max} />
      {props.hint && <span className="form-hint">{props.hint}</span>}
    </div>
  );

  const checkbox = (k, label) => (
    <label className="form-check">
      <input type="checkbox" checked={form[k]} onChange={set(k)} />
      {label}
    </label>
  );

  return (
    <AppShell breadcrumbs={breadcrumbs}>
      <span className="back-link fade-in-up" onClick={() => navigate(isEdit ? `/ctc-screenings/${id}` : '/ctc-screenings')}>
        ← {isEdit ? 'Back to Screening' : 'Back to CTC Screenings'}
      </span>

      <div className="page-header fade-in-up">
        <div>
          <div className="page-title">{isEdit ? 'Edit Screening' : 'Add CTC Screening'}</div>
          <div className="page-subtitle">
            {isEdit
              ? `Editing screening record for ${fullName}`
              : 'Only first name is required — fill in whatever the camp sheet captured'}
          </div>
        </div>
      </div>

      {error && <div className="alert alert-error fade-in-up"><Icon name="alert" size={14} /> {error}</div>}

      <form onSubmit={handleSubmit}>
        {/* Personal Information */}
        <div className="form-section fade-in-up delay-1">
          <div className="form-section-title">Personal Information</div>
          <div className="form-grid">
            {input('firstName', 'First Name', { required: true, placeholder: 'e.g. Lakshmi' })}
            {input('lastName', 'Last Name', { placeholder: 'e.g. Devi', hint: 'Needed before this person can be converted to a CTC patient' })}
            {input('dateOfBirth', 'Date of Birth', { type: 'date' })}
            {input('ageAtScreening', 'Age at Screening', { type: 'number', min: 10, max: 120, placeholder: 'Years, as recorded at camp' })}
            <div className="form-group">
              <label className="form-label">Gender</label>
              <select className="form-select" value={form.gender} onChange={set('gender')}>
                <option value="">Not recorded</option>
                <option value="FEMALE">Female</option>
                <option value="MALE">Male</option>
                <option value="OTHER">Other</option>
              </select>
              <span className="form-hint">Needed before this person can be converted to a CTC patient</span>
            </div>
            {input('phone', 'Phone', { placeholder: '+91 XXXXX XXXXX' })}
            {input('email', 'Email', { type: 'email', placeholder: 'Optional' })}
            <div className="form-group">
              <label className="form-label">Aadhar Number</label>
              <input className="form-input" value={form.aadharNumber} onChange={set('aadharNumber')}
                autoComplete="off"
                placeholder={isEdit && aadharMasked ? 'Leave blank to keep the existing number' : 'Stored encrypted'} />
              {isEdit && aadharMasked && (
                <span className="form-hint">
                  On file: {aadharRevealed !== null ? (aadharRevealed || '—') : aadharMasked}
                  {' '}
                  <button type="button" className="btn btn-sm btn-ghost" disabled={aadharRevealing} onClick={toggleAadhar}>
                    {aadharRevealing ? 'Loading…' : aadharRevealed !== null ? 'Hide' : 'Show current number'}
                  </button>
                </span>
              )}
            </div>
            {input('citizenNumber', 'Citizen Number')}
            {input('maritalStatus', 'Marital Status')}
            {input('guardianName', 'Guardian Name')}
            {input('emergencyContact', 'Emergency Contact')}
            {isEdit && (
              <div className="form-group">
                <label className="form-label">Status</label>
                {isConverted ? (
                  <input className="form-input readonly" value={statusMeta('CONVERTED_TO_PATIENT').label} readOnly />
                ) : (
                  <select className="form-select" value={form.status} onChange={set('status')}>
                    {EDITABLE_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Location & Camp Details */}
        <div className="form-section fade-in-up delay-2">
          <div className="form-section-title">Location &amp; Camp Details</div>
          <div className="form-grid">
            {input('district', 'District')}
            {input('registeredDate', 'Registered Date', { type: 'date' })}
            {input('address', 'Address', { full: true })}
            {input('campName', 'Camp Name')}
            {input('campLocation', 'Camp Location')}
            {input('referralSource', 'Referral Source')}
            {input('campaignCoordinator', 'Campaign Coordinator')}
            {input('organizerName', 'Organizer Name')}
            {input('organizerRelationship', 'Organizer Relationship')}
            {input('organizerEmail', 'Organizer Email', { type: 'email' })}
            {input('organizerPhone', 'Organizer Phone')}
            {input('markers', 'Markers', { full: true })}
          </div>
        </div>

        {/* Screening Examinations */}
        <div className="form-section fade-in-up delay-3">
          <div className="form-section-title">Screening Examinations</div>
          <div className="form-grid">
            {EXAMS.map(ex => (
              <React.Fragment key={ex.done}>
                {checkbox(ex.done, `${ex.label} done`)}
                <div className="form-group">
                  <label className="form-label">{ex.label} — result</label>
                  <input className="form-input" value={form[ex.result]} onChange={set(ex.result)} />
                </div>
              </React.Fragment>
            ))}
            {input('cervicalInfections', 'Cervical Infections')}
            {/* Free text, not a select: the backend column has no CHECK and the
                clinical team hasn't fixed a vocabulary yet. */}
            {input('overallResult', 'Overall Result')}
            <div className="form-group form-full">
              <label className="form-label">Camp Remarks</label>
              <textarea className="form-textarea" value={form.campRemarks} onChange={set('campRemarks')} />
            </div>
          </div>
        </div>

        {/* Case Follow-up Status */}
        <div className="form-section fade-in-up delay-4">
          <div className="form-section-title">Case Follow-up Status</div>
          <div className="form-grid three-col">
            {MODALITIES.map(m => (
              <div className="form-group" key={m}>
                <label className="form-label">{m}</label>
                {CASE_FLAGS.filter(f => f.modality === m).map(f => (
                  <React.Fragment key={f.key}>{checkbox(f.key, f.label)}</React.Fragment>
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="form-actions fade-in-up delay-4">
          <button className="btn btn-primary btn-lg" type="submit" disabled={saving}>
            {saving ? 'Saving…' : isEdit ? '✓ Update Screening' : '✓ Save Screening'}
          </button>
          <button className="btn btn-ghost" type="button" onClick={() => navigate(isEdit ? `/ctc-screenings/${id}` : '/ctc-screenings')}>
            Cancel
          </button>
        </div>
      </form>
    </AppShell>
  );
}
